import { z } from 'zod';

// The envelope version stored alongside the value. A later shape is a new
// version rather than a migration: an envelope this build cannot read is
// treated as absent and the browser's own selection stands.
const BLOB_VERSION = 1;

const envelopeSchema = z.object({
    version: z.literal(BLOB_VERSION),
    value: z.unknown(),
});

export type DurableFilterBlob = z.infer<typeof envelopeSchema>;

export const toDurableFilterBlob = (value: unknown): DurableFilterBlob => ({
    version: BLOB_VERSION,
    value,
});

/** Whether a stored blob carries the envelope version this build writes. */
export const isCurrentDurableFilterBlob = (blob: unknown): boolean =>
    envelopeSchema.safeParse(blob).success;

/**
 * Reads a stored blob back into a value, returning null when the envelope is
 * from another version or the value no longer satisfies the caller's schema.
 */
export const fromDurableFilterBlob = <Value>(
    blob: unknown,
    schema: z.ZodType<Value>,
): Value | null => {
    const envelope = envelopeSchema.safeParse(blob);
    if (!envelope.success) {
        return null;
    }

    const parsed = schema.safeParse(envelope.data.value);
    return parsed.success ? parsed.data : null;
};

/**
 * Serializes a value with object keys in a fixed order, so two selections that
 * hold the same members compare equal whatever order they were written in.
 */
export const stableBlobKey = (value: unknown): string => {
    if (value === undefined) {
        return 'null';
    }

    if (value === null || typeof value !== 'object') {
        return JSON.stringify(value);
    }

    if (Array.isArray(value)) {
        return `[${value.map(stableBlobKey).join(',')}]`;
    }

    const entries = Object.entries(value as Record<string, unknown>)
        .filter(([, member]) => member !== undefined)
        .sort(([left], [right]) => left.localeCompare(right));

    return `{${entries
        .map(([key, member]) => `${JSON.stringify(key)}:${stableBlobKey(member)}`)
        .join(',')}}`;
};
